import React from 'react';
import Link from 'next/link';
import slugify from 'slugify';

function toSlug(str) {
  if (!str) return '';
  return slugify(str.toString(), { lower: true, strict: true });
}

const INVALID_PERSON_SLUGS = new Set([
  'popular-lyricist',
  'popular lyricist',
  'various-artists',
  'various artists',
  'unknown',
  'not-specified',
  'not specified',
  'artist',
  'actor',
  'none',
  'na',
  'n/a',
  'null',
  'undefined',
  'traditional',
  'folk'
]);

export function EntityLink({ id, slug, name, type = 'person', className = '' }) {
  const rawId = slug || id || name;
  if (!rawId) return null;

  // If type is 'person' and name contains delimiters like '/', ',', '&', or ' and '
  if (type === 'person' && typeof rawId === 'string' && (rawId.includes('/') || rawId.includes(',') || rawId.includes('&') || /\band\b/i.test(rawId))) {
    const parts = rawId.split(/[/,&]|\band\b/i).map(p => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      return (
        <span className={className}>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              <EntityLink name={part} type={type} />
              {index < parts.length - 1 && <span className="text-foreground/75 dark:text-foreground/70 mx-1">, </span>}
            </React.Fragment>
          ))}
        </span>
      );
    }
  }

  const urlSlug = toSlug(rawId);
  const displayName = name || rawId.replace(/-/g, ' ');

  if (!urlSlug || (type === 'person' && INVALID_PERSON_SLUGS.has(urlSlug.toLowerCase().trim()))) {
    return <span className={`font-medium ${className}`}>{displayName}</span>;
  }

  const basePath = type === 'movie' ? '/movie' : '/person';

  return (
    <Link 
      href={`${basePath}/${urlSlug}`} 
      prefetch={false}
      className={`font-medium hover:text-primary transition-colors underline decoration-transparent hover:decoration-primary underline-offset-4 ${className}`}
    >
      {displayName}
    </Link>
  );
}

export function EntityLinkList({ links = [], entities = [], items = [], type = 'person', className = '', fallback = null }) {
  const rawList = items.length > 0 ? items : (links.length > 0 ? links : entities);
  
  if (!rawList || !Array.isArray(rawList)) {
    return fallback ? <span className="text-foreground/75 dark:text-foreground/70">{fallback}</span> : null;
  }

  const cleanItems = rawList.filter(item => {
    if (!item) return false;
    const name = typeof item === 'string' ? item : (item.name || item.id || '');
    return name && !INVALID_PERSON_SLUGS.has(toSlug(name).toLowerCase());
  });

  if (cleanItems.length === 0) {
    return fallback ? <span className="text-foreground/75 dark:text-foreground/70">{fallback}</span> : null;
  }

  return (
    <span className={className}>
      {cleanItems.map((item, index) => {
        let id, name;
        if (typeof item === 'string') {
          id = item;
          name = item.includes('-') ? item.replace(/-/g, ' ') : item;
        } else {
          id = item.id || item.slug || item.name;
          name = item.name || id;
        }
        
        return (
          <React.Fragment key={index}>
            <EntityLink id={id} name={name} type={type} />
            {index < cleanItems.length - 1 && <span className="text-foreground/75 dark:text-foreground/70 mx-1">, </span>}
          </React.Fragment>
        );
      })}
    </span>
  );
}
